import React, { useState, useEffect } from 'react';
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineExclamationTriangle,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark
} from 'react-icons/hi2';
import { authService } from '../../../services';
import masterDataService from '../../../services/masterDataService';
import SharedModal from '../../../components/common/SharedModal';
import PageLoading from "../../../components/common/PageLoading";
import "../PlannedMaintenanceMasters.css";

const SensorThresholdsPage = () => {
  const [thresholds, setThresholds] = useState([]);
  const [sensorTags, setSensorTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingThreshold, setEditingThreshold] = useState(null);
  const [formData, setFormData] = useState({
    tagId: '',
    conditionOperator: '',
    thresholdValue: '',
    consecutiveCount: '',
    holdTimeSec: '',
    action: '',
    severity: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();

  useEffect(() => {
    if (tenantId) {
      loadData();
    } else {
      setError('Please login to view sensor thresholds');
      setLoading(false);
    }
  }, [tenantId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [thresholdsData, tagsData] = await Promise.all([
        masterDataService.getSensorThresholds(tenantId),
        masterDataService.getSensorTags(tenantId)
      ]);
      setThresholds(thresholdsData || []);
      setSensorTags(tagsData || []);
    } catch (err) {
      setError('Failed to load data. Please try again.');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredThresholds = thresholds.filter((threshold) => {
    const tag = sensorTags.find(t => t.tagId === threshold.tagId);
    return tag?.tagName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           threshold.action?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleAdd = () => {
    setEditingThreshold(null);
    setFormData({
      tagId: '',
      conditionOperator: '',
      thresholdValue: '',
      consecutiveCount: '',
      holdTimeSec: '',
      action: '',
      severity: '',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (threshold) => {
    setEditingThreshold(threshold);
    setFormData({
      tagId: threshold.tagId?.toString() || '',
      conditionOperator: threshold.conditionOperator || '',
      thresholdValue: threshold.thresholdValue?.toString() || '',
      consecutiveCount: threshold.consecutiveCount?.toString() || '',
      holdTimeSec: threshold.holdTimeSec?.toString() || '',
      action: threshold.action || '',
      severity: threshold.severity || '',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      setError('');
      await masterDataService.deleteSensorThreshold(id);
      await loadData();
      setDeleteConfirm(null);
    } catch (err) {
      setError('Failed to delete sensor threshold. Please try again.');
      console.error('Error deleting sensor threshold:', err);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.tagId) {
      errors.tagId = 'Tag is required';
    }
    if (!formData.conditionOperator.trim()) {
      errors.conditionOperator = 'Condition operator is required';
    }
    if (!formData.thresholdValue || isNaN(parseFloat(formData.thresholdValue))) {
      errors.thresholdValue = 'Threshold value is required and must be a number';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const thresholdData = {
        tenantId: tenantId,
        tagId: parseInt(formData.tagId),
        conditionOperator: formData.conditionOperator.trim(),
        thresholdValue: parseFloat(formData.thresholdValue),
        consecutiveCount: formData.consecutiveCount ? parseInt(formData.consecutiveCount) : null,
        holdTimeSec: formData.holdTimeSec ? parseInt(formData.holdTimeSec) : null,
        action: formData.action.trim() || null,
        severity: formData.severity.trim() || null,
      };

      if (editingThreshold) {
        thresholdData.ruleId = editingThreshold.ruleId;
        await masterDataService.updateSensorThreshold(editingThreshold.ruleId, thresholdData);
      } else {
        await masterDataService.createSensorThreshold(thresholdData);
      }

      await loadData();
      setShowModal(false);
      setFormData({ tagId: '', conditionOperator: '', thresholdValue: '', consecutiveCount: '', holdTimeSec: '', action: '', severity: '' });
      setEditingThreshold(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.errors || 
                          err.message || 
                          'Failed to save sensor threshold. Please try again.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingThreshold(null);
    setFormData({ tagId: '', conditionOperator: '', thresholdValue: '', consecutiveCount: '', holdTimeSec: '', action: '', severity: '' });
    setFormErrors({});
  };

  const getTagName = (tagId) => {
    if (!tagId) return '-';
    const tag = sensorTags.find(t => t.tagId === tagId);
    return tag ? tag.tagName : '-';
  };

  if (loading) {
    return (
      <div className="pm-masters-page">
        <PageLoading message="Loading sensor thresholds..." />
      </div>
    );
  }

  return (
    <div className="pagecontainer pm-masters-page">
      <div className="page-header">
        <div className="header-content">
          <h1>Sensor Thresholds</h1>
          <p>Manage sensor thresholds for Planned Maintenance</p>
        </div>
        <button className="btn-primary" onClick={handleAdd}>
          <HiOutlinePlus />
          Add Threshold
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <div className="table-header">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search thresholds..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
          <div className="table-info">
            {filteredThresholds.length} {filteredThresholds.length === 1 ? 'threshold' : 'thresholds'}
          </div>
        </div>

        {filteredThresholds.length === 0 ? (
          <div className="empty-state">
            <HiOutlineExclamationTriangle className="empty-icon" />
            <h3>No thresholds found</h3>
            <p>
              {searchTerm
                ? 'Try adjusting your search terms'
                : 'Get started by creating your first sensor threshold'}
            </p>
            {!searchTerm && (
              <button className="btn-primary" onClick={handleAdd}>
                Add Threshold
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Tag</th>
                <th>Condition Operator</th>
                <th>Threshold Value</th>
                <th>Action</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredThresholds.map((threshold) => (
                <tr key={threshold.ruleId}>
                  <td>{threshold.ruleId}</td>
                  <td>{getTagName(threshold.tagId)}</td>
                  <td>{threshold.conditionOperator}</td>
                  <td>{threshold.thresholdValue}</td>
                  <td>{threshold.action || '-'}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon-edit"
                        onClick={() => handleEdit(threshold)}
                        title="Edit"
                      >
                        <HiOutlinePencil />
                      </button>
                      <button
                        className="btn-icon-delete"
                        onClick={() => setDeleteConfirm(threshold)}
                        title="Delete"
                      >
                        <HiOutlineTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create/Edit Modal */}
      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingThreshold ? 'Edit Sensor Threshold' : 'Add New Sensor Threshold'}
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="sensor-threshold-form"
              className="btn-primary"
              disabled={submitting}
            >
              {submitting
                ? editingThreshold
                  ? 'Updating...'
                  : 'Creating...'
                : editingThreshold
                ? 'Update'
                : 'Create'}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="sensor-threshold-form" onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label htmlFor="tagId">
                Tag <span className="required">*</span>
              </label>
              <select
                id="tagId"
                value={formData.tagId}
                onChange={(e) =>
                  setFormData({ ...formData, tagId: e.target.value })
                }
                className={formErrors.tagId ? 'error' : ''}
              >
                <option value="">Select Tag</option>
                {sensorTags.map((tag) => (
                  <option key={tag.tagId} value={tag.tagId.toString()}>
                    {tag.tagName}
                  </option>
                ))}
              </select>
              {formErrors.tagId && (
                <span className="field-error">{formErrors.tagId}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="conditionOperator">
                Condition Operator <span className="required">*</span>
              </label>
              <input
                type="text"
                id="conditionOperator"
                value={formData.conditionOperator}
                onChange={(e) =>
                  setFormData({ ...formData, conditionOperator: e.target.value })
                }
                className={formErrors.conditionOperator ? 'error' : ''}
                placeholder="e.g., >, <, >=, <="
              />
              {formErrors.conditionOperator && (
                <span className="field-error">{formErrors.conditionOperator}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="thresholdValue">
                Threshold Value <span className="required">*</span>
              </label>
              <input
                type="number"
                id="thresholdValue"
                value={formData.thresholdValue}
                onChange={(e) =>
                  setFormData({ ...formData, thresholdValue: e.target.value })
                }
                className={formErrors.thresholdValue ? 'error' : ''}
                placeholder="Enter threshold value"
                step="0.01"
              />
              {formErrors.thresholdValue && (
                <span className="field-error">{formErrors.thresholdValue}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="consecutiveCount">Consecutive Count</label>
              <input
                type="number"
                id="consecutiveCount"
                value={formData.consecutiveCount}
                onChange={(e) =>
                  setFormData({ ...formData, consecutiveCount: e.target.value })
                }
                placeholder="Enter consecutive count (optional)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="holdTimeSec">Hold Time (sec)</label>
              <input
                type="number"
                id="holdTimeSec"
                value={formData.holdTimeSec}
                onChange={(e) =>
                  setFormData({ ...formData, holdTimeSec: e.target.value })
                }
                placeholder="Enter hold time in seconds (optional)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="action">Action</label>
              <input
                type="text"
                id="action"
                value={formData.action}
                onChange={(e) =>
                  setFormData({ ...formData, action: e.target.value })
                }
                placeholder="Enter action (optional)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="severity">Severity</label>
              <input
                type="text"
                id="severity"
                value={formData.severity}
                onChange={(e) =>
                  setFormData({ ...formData, severity: e.target.value })
                }
                placeholder="Enter severity (optional)"
              />
            </div>

          </form>
        </div>
      </SharedModal>

      {/* Delete Confirmation Modal */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        customClass="small"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-danger"
              onClick={() => handleDelete(deleteConfirm.ruleId)}
            >
              Delete
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>
            Are you sure you want to delete this threshold rule?
          </p>
          <p className="warning-text">
            This action cannot be undone.
          </p>
        </div>
      </SharedModal>
    </div>
  );
};

export default SensorThresholdsPage;
